// File: src/components/CreateTestModal.jsx
import { useMemo, useState, useEffect } from "react";
import { createTestDoc, fetchQStatsForAssignments } from "../api";
import {
  buildQuestionPool,
  pickLowestBias,
  deriveAnswerKeyCandidates,
  sanitize,
} from "../utils";
import { RxPlusCircled } from "react-icons/rx";
import Select from "./Select";

export default function CreateTestModal({ onClose, owner, assignmentsJson }) {
  const [numQuestions, setNumQuestions] = useState(15);
  const [timeMins, setTimeMins] = useState(20);
  const [title, setTitle] = useState("New Test 1");
  const [isPublic, setIsPublic] = useState(true);
  const [sharedWithText, setSharedWithText] = useState("");
  const [creating, setCreating] = useState(false);

  const [chapterList, setChapterList] = useState([]);
  const [selectedList, setSelectedList] = useState([
    {
      id: 1,
      chapterSelected: "",
      assignmentSelected: "",
    },
  ]);
  const [errorMsg, setErrorMsg] = useState("");

  // update chapter list when assignmentsJson changes
  useEffect(() => {
    setChapterList(assignmentsJson?.chapters || []);
  }, [assignmentsJson]);

  // Build the question pool from current selectedList using your util
  const pool = useMemo(() => {
    try {
      const chapters = assignmentsJson?.chapters || [];

      const newArr = selectedList
        .map((item) => {
          if (!item.chapterSelected || !item.assignmentSelected) return null;
          const chapter = chapters.find(
            (ch) => ch.name === item.chapterSelected
          );
          if (!chapter) return null;
          const assignment = chapter.assignments.find(
            (asg) => asg.name === item.assignmentSelected
          );
          if (!assignment) return null;

          return {
            name: item.chapterSelected,
            displayName: chapter.displayName,
            assignments: [
              {
                name: item.assignmentSelected,
                displayName: assignment.displayName,
                length: assignment.length,
              },
            ],
          };
        })
        .filter(Boolean);

      // buildQuestionPool returns array of { chapter, assignment, qno, assignmentLength }
      return buildQuestionPool(newArr);
    } catch (e) {
      console.error("build pool failed", e);
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedList, assignmentsJson]);

  // helper to fetch answer_key.json for an assignment (cached)
  async function fetchAnswerKeyJson(chapter, assignment, cache) {
    const key = `${chapter}__${assignment}`;
    if (cache[key] !== undefined) return cache[key];
    try {
      const url = `/images/${encodeURIComponent(chapter)}/${encodeURIComponent(
        assignment
      )}/answer_key.json`;
      const res = await fetch(url);
      if (!res.ok) {
        // no answer key present
        cache[key] = null;
        return null;
      }
      const json = await res.json();
      cache[key] = json;
      return json;
    } catch (e) {
      console.warn("failed to fetch answer_key.json", chapter, assignment, e);
      cache[key] = null;
      return null;
    }
  }

  // create handler
  async function handleCreate() {
    setErrorMsg("");
    setCreating(true);
    try {
      if (!pool || pool.length === 0) {
        setErrorMsg(
          "No questions in the selected chapters/assignments. Please select valid items."
        );
        setCreating(false);
        return;
      }

      if (numQuestions <= 0) {
        setErrorMsg("Number of questions must be at least 1.");
        setCreating(false);
        return;
      }

      if (numQuestions > pool.length) {
        setErrorMsg(
          `Requested ${numQuestions} questions but only ${pool.length} available in the selected items. Reduce number of questions or add more assignments.`
        );
        setCreating(false);
        return;
      }

      // --- BIASING: fetch qstats only for assignments present in pool ---
      // collect unique assignment docIds
      const assignmentKeys = Array.from(
        new Set(pool.map((p) => `${p.chapter}___${p.assignment}`))
      );
      const qstatInputs = assignmentKeys.map((k) => {
        // we can pass docId strings; API supports both
        return k;
      });

      const qstatsMap = await fetchQStatsForAssignments(qstatInputs);
      // annotate pool items with frequency
      const poolAnnotated = pool.map((p) => {
        const docId = `${p.chapter}___${p.assignment}`;
        const statDoc = qstatsMap[docId] || {};
        const freq = Number(statDoc[String(p.qno)] || 0);
        return { ...p, _qfreq: freq };
      });

      // pick biased sample: prefer lowest frequency first (random within same freq)
      const sampled = pickLowestBias(poolAnnotated, numQuestions);

      // cache answer_key.json per assignment to avoid repeated fetching
      const akCache = {};

      // construct questions array - same shape as before
      const questions = [];
      for (const q of sampled) {
        const ak = await fetchAnswerKeyJson(q.chapter, q.assignment, akCache);

        let questionType = "SMCQ";
        let answerKey = null;
        let isSolutionAvailable = false;
        let solutionImage = null;

        if (ak && ak[q.qno]) {
          const entry = ak[q.qno];
          questionType = entry.type || entry.questionType || questionType;
          answerKey = entry.answer ?? entry.key ?? null;
          isSolutionAvailable =
            !!entry.isSolutionAvailable || !!entry.hasSolution;
        }

        if (isSolutionAvailable) {
          solutionImage = `/images/${sanitize(q.chapter)}/${sanitize(
            q.assignment
          )}/solutions/${q.qno}.png`;
        }

        questions.push({
          chapter: q.chapter,
          assignment: q.assignment,
          qno: q.qno,
          assignmentLength: q.assignmentLength,
          questionType,
          answerKey,
          isSolutionAvailable,
          solutionImage,
          answerKeyCandidates: deriveAnswerKeyCandidates(
            q.chapter,
            q.assignment,
            q.qno,
            q.assignmentLength
          ),
          questionImage: `/images/${sanitize(q.chapter)}/${sanitize(
            q.assignment
          )}/${q.qno}.png`,
        });
      }

      // prepare shared list
      const sharedList = (sharedWithText || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);

      const ownerUid = owner ? owner.uid : "anonymous";
      const ownerEmail = owner ? owner.email : "anonymous";

      // createTestDoc now also applies qstats increments in a batched commit
      const testId = await createTestDoc({
        ownerUid,
        ownerEmail,
        title,
        isPublic,
        sharedWith: sharedList,
        questions,
        timeLimitMins: timeMins,
      });

      setCreating(false);
      onClose(testId);
    } catch (e) {
      console.error("create test failed", e);
      setErrorMsg("Failed to create test. See console for details.");
      setCreating(false);
    }
  }

  const deleteSelect = (idToRemove) => {
    let newArr = selectedList.filter((item) => item.id !== idToRemove);
    newArr = newArr.map((item, index) => ({ ...item, id: index + 1 }));

    if (newArr.length === 0) return;

    setSelectedList(newArr);
  };

  const handleAddToList = () => {
    setSelectedList((prev) => [
      ...prev,
      {
        id: prev[prev.length - 1]?.id + 1 || 1,
        chapterSelected: "",
        assignmentSelected: "",
      },
    ]);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50 overflow-auto">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl">
        <h3 className="text-xl font-semibold mb-4">Create Test</h3>
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2">
            Title
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full border rounded p-2 mt-1"
            />
          </label>
          <label>
            Number of Questions
            <input
              type="number"
              value={numQuestions}
              onChange={(e) => setNumQuestions(parseInt(e.target.value || 0))}
              className="w-full border rounded p-2 mt-1"
            />
          </label>
          <label>
            Time (mins)
            <input
              type="number"
              value={timeMins}
              onChange={(e) => setTimeMins(parseInt(e.target.value || 0))}
              className="w-full border rounded p-2 mt-1"
            />
          </label>

          {selectedList.map((val, index) => (
            <Select
              key={val.id}
              id={val.id}
              chapterList={chapterList}
              deleteSelect={deleteSelect}
              setSelectedList={setSelectedList}
            />
          ))}

          <div className="col-span-2">
            <button
              className="flex gap-2 items-center hover:text-blue-600 transition"
              onClick={handleAddToList}
            >
              <span className="text-xl">
                <RxPlusCircled />
              </span>{" "}
              Add more
            </button>
          </div>

          <span className="text-sm text-yellow-600 col-span-2">
            * Selecting the same assignment multiple times increases chance of
            its questions appearing but there are chances that same question may
            be repeated twice
          </span>
          <label className="col-span-2">
            Visibility
            <div className="flex items-center gap-3 mt-1">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={!isPublic}
                  onChange={() => setIsPublic(false)}
                />{" "}
                Private
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  checked={isPublic}
                  onChange={() => setIsPublic(true)}
                />{" "}
                Public
              </label>
            </div>
          </label>

          <label className="col-span-2">
            Share with (comma separated UIDs or emails)
            <input
              value={sharedWithText}
              onChange={(e) => setSharedWithText(e.target.value)}
              className="w-full border rounded p-2 mt-1"
            />
          </label>
        </div>

        {errorMsg && (
          <div className="mt-3 text-sm text-red-600">{errorMsg}</div>
        )}

        <div className="mt-4 flex justify-end gap-3">
          <button className="px-4 py-2 rounded" onClick={() => onClose(null)}>
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded bg-green-600 text-white"
            onClick={handleCreate}
            disabled={creating}
          >
            {creating ? "Creating..." : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
